import clsx from "clsx";
import { ListCard } from "./Card/listCard";
import { SearchField } from "./searchField";
import { RiHomeLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaPlus } from "react-icons/fa";
import { LoadingCircle } from "../common/loading";
import { SortOptions } from "../common/sortOptions";
import { ListCardSidebar } from "./listCardSidebar";
import { SearchMapView } from "./SearchMap/searchMap";
import { Header } from "../common/BrowseListing/header";
import { useMapStore } from "../../stores/mapCordinations";
import { NoListingsCard } from "./NoListings/noListingsCard";
import { useFilterPanelStore } from "../../stores/filterStore";
import { useMapFiltersStore } from "../../stores/filterMapStore";
import { usePaginationStore } from "../../stores/paginationStore";
import { usePropertyStore } from "../../stores/newStores/cartComparisonStore";
import {
  useFilteredPropertiesStore,
  useListingStore,
} from "../../stores/exploreListings";
import {
  type MouseEvent,
  type TouchEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import "swiper/css";
import "./exploreListingsViewStyles.css";
import { useShowLoginModalStore } from "../../stores/loginModalStore";
import { LoginModal } from "../LoginModal/loginModal";

const BuyerExploreListingsView = () => {
  const [loading, setLoading] = useState(false);
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [selectedSortOption, setSelectedSortOption] = useState("Default");
  const { mapOpen, setMapOpen } = useListingStore();
  const { filterPanelVisible } = useFilterPanelStore();
  const optionsRef = useRef<HTMLDivElement | null>(null);
  const { currentPage, setCurrentPage, setPageStepList, pageStepList } =
    usePaginationStore();

  const { filteredProperties, setFilteredProperties } =
    useFilteredPropertiesStore();
  const {
    reloadMapCities,
    setReloadMapCities,
    setSortFilter,
    switchMode,
    setSwitchMode,
    openFilterPanel,
  } = useMapFiltersStore();

  const { limitMap, showSlider } = useMapStore();

  const {
    compareCount,
    setCompareCount,
    showAddEffect,
    setShowAddEffect,
    selectedIds,
    setFilteredPropertiesCompare,
    filteredPropertiesCompare,
    showListCard,
    compareListId,
  } = usePropertyStore();
  const navigate = useNavigate();

  const toggleOptions = () => {
    setShowSortOptions((prev) => !prev);
  };

  const [startY, setStartY] = useState<number | null>(null);

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    setStartY(event.touches[0].clientY);
  };
  useEffect(() => {
    if (mapOpen) {
      setSelectedSortOption("Default");
    }
  }, [mapOpen]);
  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (startY !== null) {
      const currentY = event.touches[0].clientY;
      if (startY - currentY > 50) {
        setMapOpen(false);
        setStartY(null);
      }
    }
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (startY !== null) {
      const currentY = event.clientY;
      if (startY - currentY > 50) {
        setMapOpen(false);
        setStartY(null);
      }
    }
  };

  const handleMouseDown = (event: MouseEvent) => {
    setStartY(event.clientY);
  };

  useEffect(() => {
    if (reloadMapCities) {
      setFilteredProperties([]);
      setReloadMapCities(false);
    }
  }, [reloadMapCities, setReloadMapCities, setFilteredProperties, switchMode]);

  useEffect(() => {
    if (showSortOptions && optionsRef.current) {
      const optionsRect = optionsRef.current.getBoundingClientRect();
      const isOffScreen = optionsRect.right > window.innerWidth;

      if (isOffScreen) {
        optionsRef.current.style.left = `-${
          optionsRect.right - window.innerWidth + 20
        }px`;
      }
    }
  }, [showSortOptions]);

  useEffect(() => {
    const defaultPage = "1";
    const defaultLimit = "100";

    switch (true) {
      case selectedSortOption === "Default": {
        setFilteredProperties([]);
        setSortFilter({
          page: defaultPage,
          limit: defaultLimit,
          sortBy: "date",
          sortOrder: "desc",
        });
        break;
      }
      case selectedSortOption === "Newest": {
        setFilteredProperties([]);
        setSortFilter({
          page: defaultPage,
          limit: defaultLimit,
          sortBy: "date",
          sortOrder: "desc",
        });
        break;
      }
      case selectedSortOption === "Oldest": {
        setFilteredProperties([]);
        setSortFilter({
          page: defaultPage,
          limit: defaultLimit,
          sortBy: "date",
          sortOrder: "asc",
        });
        break;
      }
      case selectedSortOption === "Price Low to High": {
        setFilteredProperties([]);
        setSortFilter({
          page: defaultPage,
          limit: defaultLimit,
          sortBy: "price",
          sortOrder: "asc",
        });
        break;
      }
      case selectedSortOption === "Price High to Low": {
        setFilteredProperties([]);
        setSortFilter({
          page: defaultPage,
          limit: defaultLimit,
          sortBy: "price",
          sortOrder: "desc",
        });
        break;
      }
      default:
        break;
    }
  }, [selectedSortOption, setFilteredProperties, setSortFilter, switchMode]);

  const handleAdd = async () => {
    const newFilteredProperties = selectedIds.filter(
      (listingId) =>
        !filteredPropertiesCompare.some(
          (existingProperty) => existingProperty.listingId === listingId
        )
    );

    const newAdditions = newFilteredProperties.length;

    setCompareCount(newAdditions);
    setShowAddEffect(newAdditions > 0);

    setFilteredPropertiesCompare((prev) => [
      ...prev,
      ...newFilteredProperties.map((listingId) => ({
        listingId,
      })),
    ]);
  };

  useEffect(() => {
    if (showAddEffect) {
      setTimeout(() => {
        setShowAddEffect(false);
      }, 3000);
    }
  }, [showAddEffect]);

  const propertiesPerPage = Math.min(20, filteredProperties.length);
  const maxStepsPerFetch = Math.min(
    25,
    Math.ceil(filteredProperties.length / propertiesPerPage)
  );

  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);

  const propertiesToDisplay = useMemo(() => {
    const startIndex = (pageStepList - 1) * propertiesPerPage;
    const endIndex = startIndex + propertiesPerPage;
    return filteredProperties.slice(startIndex, endIndex);
  }, [filteredProperties, pageStepList, currentPage, propertiesPerPage]);

  const goToPreviousPage = () => {
    if (pageStepList === 1 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setPageStepList(1);
    } else if (pageStepList > 1) {
      setPageStepList(pageStepList - 1);
    }
  };

  const goToNextPage = () => {
    if (pageStepList === maxStepsPerFetch) {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
        setPageStepList(1);
      }
    } else if (pageStepList < maxStepsPerFetch) {
      setPageStepList(pageStepList + 1);
    }
  };
  const getPaginationRange = () => {
    const totalPageNumbers = Math.min(5, totalPages);
    const pageOffset = (currentPage - 1) * maxStepsPerFetch;

    let start = Math.max(
      pageOffset + pageStepList - Math.floor(totalPageNumbers / 2),
      pageOffset + 1
    );
    let end = Math.min(
      start + totalPageNumbers - 1,
      pageOffset + maxStepsPerFetch
    );

    if (end - start + 1 < totalPageNumbers) {
      start = Math.max(end - totalPageNumbers + 1, pageOffset + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  useEffect(() => {
    setPageStepList(1);
  }, [currentPage]);

  const paginationRange = getPaginationRange();
  const [showOfferBanner, setShowOfferBanner] = useState(false);
  const { showLoginModal } = useShowLoginModalStore();


  return (
    <div
      className={`relative ${
        switchMode === "map" && mapOpen && "overflow-hidden h-[100vh]"
      } ${switchMode === "list" && "overflow-y-auto h-[100vh] pb-14"} ${
        !mapOpen && "pb-24"
      }`}
    >
      <div
        className={clsx(
          switchMode === "list" && "fixed top-0 w-full",
          showSlider ? "z-[40]" : "z-50"
        )}
      >
        <Header
          showOfferBanner={showOfferBanner}
          setShowOfferBanner={setShowOfferBanner}
        />
      </div>
      <div className={clsx(switchMode === "list" && "fixed top-0 w-full z-40")}>
        <div
          className={`w-full h-[92px] absolute inset-x-0 ${
            !showOfferBanner ? "top-[88px]" : "top-[145px]"
          } z-[11]`}
        >
          <SearchField switchMode={switchMode} setSwitchMode={setSwitchMode} />
        </div>
      </div>
      <SearchMapView loading={loading} setLoading={setLoading} />
      {switchMode === "map" && mapOpen && (
        <div className="h-full">
          <ListCardSidebar
            filteredProperties={filteredProperties}
            showSortOptions={showSortOptions}
            optionsRef={optionsRef}
            toggleOptions={toggleOptions}
            selectedSortOption={selectedSortOption}
            setSelectedSortOption={setSelectedSortOption}
            loading={loading}
            showOfferBanner={showOfferBanner}
          />
        </div>
      )}
      {switchMode === "list" && mapOpen && (
        <div className="bg-white absolute inset-0 z-30 h-[100vh]">
          <div className="container mx-auto pt-[210px] w-full flex flex-col pb-10">
            <div className="flex justify-between items-center w-full">
              <div className="text-xl font-semibold text-z-shark">
                {/* Featured listings in Toronto, ON */}
              </div>
              <div className="flex items-center gap-10">
                <div className="text-z-shark text-sm flex items-center">
                  Results:
                  <div className="text-z-shark flex items-center font-normal pl-1">
                    {filteredProperties?.length === limitMap
                      ? `${filteredProperties?.length}+`
                      : filteredProperties?.length}
                  </div>
                </div>
                <div className="sort-options-container">
                  <SortOptions
                    desktopMode={true}
                    showOptions={showSortOptions}
                    optionsRef={optionsRef}
                    onToggleOptions={toggleOptions}
                    selectedOption={selectedSortOption}
                    setSelectedOption={setSelectedSortOption}
                  />
                </div>
              </div>
            </div>
            {loading ? (
              <div className="flex justify-center items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <LoadingCircle />
              </div>
            ) : (
              <div className="">
                <div className="w-full mt-4 grid grid-cols-3 xl:grid-cols-4 gap-3 xl:gap-3 gap-y-7">
                  {propertiesToDisplay.length > 0 ? (
                    propertiesToDisplay.map((property) => (
                      <ListCard key={property.listingId} property={property} />
                    ))
                  ) : (
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      No properties found.
                    </div>
                  )}
                </div>
                {propertiesToDisplay.length > 0 && (
                  <div className="flex justify-center items-center mt-6 border border-gray-300 rounded-md w-fit mx-auto">
                    {/* biome-ignore lint/a11y/useButtonType: <explanation> */}
                    <button
                      onClick={goToPreviousPage}
                      className="px-4 py-2 rounde"
                    >
                      Previous
                    </button>
                    {paginationRange.map((page) => (
                      // biome-ignore lint/a11y/useButtonType: <explanation>
                      <button
                        key={page}
                        onClick={() => {
                          const newStep =
                            page - (currentPage - 1) * maxStepsPerFetch;
                          if (newStep > 0 && newStep <= maxStepsPerFetch) {
                            setPageStepList(newStep);
                          }
                        }}
                        className={`px-4 py-2 border-x border-gray-300 ${
                          page ===
                          (currentPage - 1) * maxStepsPerFetch + pageStepList
                            ? "bg-z-dark-moss-green text-white font-bold"
                            : "bg-white hover:bg-gray-100"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    {/* biome-ignore lint/a11y/useButtonType: <explanation> */}
                    <button
                      onClick={goToNextPage}
                      disabled={
                        filteredProperties.length < 500 &&
                        pageStepList === totalPages
                      }
                      className={`px-4 py-2 rounded ${
                        pageStepList === totalPages ? "text-gray-500" : " "
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {!mapOpen && (
        <div className="listings-container translate-y-0">
          <div
            className={`w-full flex flex-col gap-4 relative ${
              filterPanelVisible ? "overflow-hidden" : "overflow-scroll"
            }`}
          >
            <div className="flex items-center justify-between p-4 shadow-sm">
              <span className="text-black font-semibold">
                {propertiesToDisplay?.length} Listings
              </span>
              <SortOptions
                showOptions={showSortOptions}
                optionsRef={optionsRef}
                onToggleOptions={toggleOptions}
                selectedOption={selectedSortOption}
                setSelectedOption={setSelectedSortOption}
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-full w-full">
                <LoadingCircle />
              </div>
            ) : (
              propertiesToDisplay.map((property) => (
                <div key={property.listingId} className="px-2">
                  <ListCard key={property.listingId} property={property} />
                </div>
              ))
            )}

            {!loading && propertiesToDisplay.length === 0 && (
              <>
                <div>No Listings Card</div>
                <NoListingsCard />
              </>
            )}
            {loading && (
              <div className="flex items-center justify-center h-full w-full">
                <LoadingCircle />
              </div>
            )}
          </div>
        </div>
      )}
      {/* "-bottom-full" */}
      <div
        className={clsx(
          "fixed flex items-center space-x-2 transition-[all_0.5s_ease-in]",
          selectedIds.length > 0 ? "bottom-10" : "-bottom-full",
          showListCard
            ? "w-[calc(100%-444px)] right-0 justify-center"
            : "w-full justify-center right-0",
          switchMode === "list" && "right-1/2 translate-x-1/2",
          openFilterPanel ? "z-[5]" : "z-40"
        )}
      >
        {switchMode === "list" && (
          <button
            type="button"
            className={clsx(
              "py-4 px-5 rounded-[28px] cursor-pointer",
              "text-white flex items-center space-x-1 font-semibold text-xl whitespace-nowrap",
              "shadow-[0_6px_14px_0_rgba(0,0,0,0.25)]",
              "transform transition-all duration-300 ease-in-out",
              "hover:scale-[1.02]",
              showAddEffect
                ? "bg-[#50AA26] pointer-events-none"
                : "bg-z-prussian-blue",
              showAddEffect ? "w-[210px]" : "w-[100px]"
            )}
            onClick={handleAdd}
          >
            <div className="transition-all duration-300">
              {showAddEffect ? (
                <FaCheck className="text-lg" />
              ) : (
                <FaPlus className="text-lg" />
              )}
            </div>
            <span
              className={clsx(
                "transition-all duration-300",
                showAddEffect && "animate-[fadeIn_0.3s_ease-in-out]"
              )}
            >
              {showAddEffect ? `Added ${compareCount} homes` : "Add"}
            </span>
          </button>
        )}
        <button
          type="button"
          className={clsx(
            "py-4 px-5 rounded-[28px] cursor-pointer bg-z-prussian-blue",
            "text-white flex items-center space-x-1 font-semibold text-xl",
            "shadow-[0_6px_14px_0_rgba(0,0,0,0.25)]",
            "transform transition-all duration-300 ease-in-out",
            "hover:scale-[1.02]",
            compareListId.length > 0 ? "flex" : "hidden"
          )}
          onClick={() => navigate("/compare")}
        >
          <RiHomeLine className="text-2xl" />
          <span>Comparison list</span>
        </button>
      </div>
      {showLoginModal && <LoginModal />}
    </div>
  );
};

export { BuyerExploreListingsView };
